import { makeEnum, makeOptions,
  setupClass, lookupData, 
} from '../Base.js'

import * as psy from './Psychrometrics.js'
import * as calc from './ResidentialCalculations.js'
import { CalcContext } from '../Common/CalcContext.js'

export { Units } from '../Common/Units.js'

import {
  FieldType,
  Field,
  FieldGroup,
} from '../Common/Field.js'

import {
  ManualOrAutomatic,
  AutomaticOrManual,
  YesNo,
 } from './Common.js'


export let RecoveryType = makeEnum({
  None: 'None',
  HRV: 'HRV',
  ERV: 'ERV',
})

let ResidentialRecoveryType = makeEnum({
  None: 'None',
  HRV: 'HRV',
})

let BuildingQuality = makeEnum({
  Tight: 'Tight',
  Good: 'Good',
  Average: 'Average',
  Leaky: 'Leaky',
  VeryLeaky: 'VeryLeaky',
})

export class HouseVentilationInfiltration {
  init(proj) {
    this.proj = proj;

    this.entryMethod = Field.makeSelect('Entry Method', AutomaticOrManual, {
      bold: true,
      basePath: 'ventilation',
    })
    // Manual:
    this.totalVentilation = new Field({
      name: 'Total Ventilation',
      type: FieldType.AirFlow,
      defaultValue: 100,
      metricDefaultValue: 50,
      min: 0,
      allowMin: true,
      basePath: 'ventilation',
    });
    this.totalVentilation.makeUpdater((field) => {
      field.visible = this.entryMethod.value == AutomaticOrManual.Manual;
    });

    // When using automatic method, show the calculated value
    this.outputVentilation = new Field({
      name: 'Total Ventilation',
      type: FieldType.AirFlow,
      basePath: 'ventilation',
      isOutput: true,
    })
    this.outputVentilation.makeUpdater((field) => {
      if (this.entryMethod.value == AutomaticOrManual.Automatic) {
        field.visible = true;
        let ctx = CalcContext.create();
        let toplevelData = this.proj.toplevelData;
        field.value = calc.calcQ_V(ctx, toplevelData.totalFloorArea.value,
          toplevelData.numberBedrooms.value);
      } else {
        field.visible = false;
      }
    })
    
    this.ventilationHelpInfo = {
      helpText: `The automatic ventilation calculation follows ASHRAE Standard 62.2, which uses the formula:
      
      Ventilation (CFM) = (0.03 × Floor Area) + (7.5 × (Number of Bedrooms + 1))
      
      Where:
      - Floor Area is in square feet
      - The formula accounts for the number of bedrooms plus one to estimate the number of occupants
      
      This calculation ensures adequate fresh air for both the building size and expected occupancy.`,
    }
    
    this.continuousExhaust = new Field({
      name: 'Continuous Exhaust',
      type: FieldType.AirFlow,
      defaultValue: 0,
      min: 0,
      allowMin: true,
      basePath: 'ventilation',
    });

    // Heat recovery:
    this.recoveryType = Field.makeSelect('Recovery Type', ResidentialRecoveryType, {bold: true})
    this.hrvGroup = new FieldGroup([
      new Field({
        key: 'flow',
        name: 'Airflow',
        type: FieldType.AirFlow,
        defaultValue: 100,
        metricDefaultValue: 50,
        min: 0,
        allowMin: true,
        isOutput: true,
      }),
      new Field({
        key: 'summerEfficiency',
        name: 'Summer Efficiency',
        type: FieldType.Percent,
        defaultValue: 55,
      }),
      new Field({
        key: 'winterEfficiency',
        name: 'Winter Efficiency',
        type: FieldType.Percent,
        defaultValue: 75,
      })
    ], {
      basePath: 'ventilation',
    });
    let hrvFlow = this.hrvGroup.getField('flow');
    hrvFlow.data = {defaultValue: hrvFlow.value, useDefault: true};
    hrvFlow.makeUpdater((field) => {
      let ventilation = this.entryMethod.value == AutomaticOrManual.Automatic
        ? this.outputVentilation.value
        : this.totalVentilation.value;
      field.data.defaultValue = ventilation;
      field.isOutput = field.data.useDefault;
      if (field.data.useDefault) {
        field.value = ventilation;
      }
    });
    this.hrvGroup.setVisibility(() => {
      return this.recoveryType.value == RecoveryType.HRV;
    })

    this.ervGroup = new FieldGroup([
      new Field({
        key: 'flow',
        name: 'Flow',
        type: FieldType.AirFlow,
        defaultValue: 100,
        metricDefaultValue: 50,
        min: 0,
        allowMin: true,
      }),
      new Field({
        key: 'summerSensibleEfficiency',
        name: 'Summer sensible efficiency',
        type: FieldType.Percent,
        defaultValue: 55,
      }),
      new Field({
        key: 'summerTotalEfficiency',
        name: 'Summer total efficiency',
        type: FieldType.Percent,
        defaultValue: 60,
      }),
      new Field({
        key: 'winterSensibleEfficiency',
        name: 'Winter sensible efficiency',
        type: FieldType.Percent,
        defaultValue: 75,
      }),
      new Field({
        key: 'winterTotalEfficiency',
        name: 'Winter total efficiency',
        type: FieldType.Percent,
        defaultValue: 80,
      }),
    ], {
      basePath: 'ventilation',
    });
    this.ervGroup.setVisibility(() => {
      return this.recoveryType.value == RecoveryType.ERV;
    })

    this.otherBalancedAirflows = new Field({
      name: "Other Balanced Airflows",
      type: FieldType.AirFlow,
      defaultValue: 0,
      min: 0,
      allowMin: true,
      basePath: 'ventilation',
    });

    this.infiltrationEntryMethod = Field.makeSelect('Entry Method', AutomaticOrManual, {
      bold: true,
      basePath: 'infiltration',
    })
    this.infiltrationManual = new FieldGroup([
      new Field({
        key: 'summerInfiltration',
        name: 'Summer Infiltration',
        type: FieldType.AirFlow,
        min: 0,
        allowMin: true,
      }),
      new Field({
        key: 'winterInfiltration',
        name: 'Winter Infiltration',
        type: FieldType.AirFlow,
        min: 0,
        allowMin: true,
      })
    ], {
      basePath: 'infiltration',
    });
    this.infiltrationManual.setVisibility(() => {
      return this.infiltrationEntryMethod.value == ManualOrAutomatic.Manual;
    })
    this.infiltrationAutomatic = new FieldGroup([
      new Field({
        key: 'totalExposedArea',
        name: 'Total Exposed Building Area',
        type: FieldType.Area,
        defaultValue: 3500,
        metricDefaultValue: 350,
        min: 0,
        allowMin: true,
      }),
      new Field({
        key: 'buildingQuality',
        name: 'Building Construction Quality',
        type: FieldType.Select,
        choices: makeOptions(BuildingQuality),
        defaultValue: BuildingQuality.Good,
      }),
      new Field({
        key: 'fluePipe',
        name: 'Flue Pipe',
        type: FieldType.Select,
        choices: makeOptions(YesNo),
        defaultValue: YesNo.No,
      }),
      new Field({
        key: 'fluePipeLeakageArea',
        name: 'Flue Pipe Effective Area',
        type: FieldType.SmallArea,
        defaultValue: 0,
        min: 0,
        allowMin: true,
        isOutput: true,
      })
    ], {
      basePath: 'infiltration',
    });
    let exposedArea = this.infiltrationAutomatic.getField('totalExposedArea');
    exposedArea.data = {defaultValue: exposedArea.value, useDefault: true};
    exposedArea.makeUpdater((field) => {
      let area = this.proj.walls.reduce((sum, wall) => sum + wall.getArea(), 0)
        + this.proj.roofs.reduce((sum, roof) => sum + roof.getArea(), 0);
      field.data.defaultValue = area;
      field.isOutput = field.data.useDefault;
      if (field.data.useDefault) {
        field.value = area;
      }
    });
    let fluePipe = this.infiltrationAutomatic.getField('fluePipe');
    let fluePipeArea = this.infiltrationAutomatic.getField('fluePipeLeakageArea');
    fluePipeArea.data = {defaultValue: fluePipeArea.value, useDefault: true};
    fluePipeArea.makeUpdater((field) => {
      let defaultValue = fluePipe.value === YesNo.Yes ? 7.1 : 0;
      field.data.defaultValue = defaultValue;
      field.isOutput = field.data.useDefault;
      if (field.data.useDefault) {
        field.value = defaultValue;
      }
    });
    this.infiltrationAutomatic.setVisibility(() => {
      return this.infiltrationEntryMethod.value == ManualOrAutomatic.Automatic; 
    })
    
    this.infiltrationHelpInfo = {
      helpText: `When the automatic entry method is selected, we use ASHRAE standard calculations to determine the summer and winter infiltration values.
      
      The calculation takes into account:
      - Total exposed building area
      - Building construction quality
      - Flue pipe leakage area
      - Indoor and outdoor temperature differences
      - Number of stories and ceiling height
      
      These factors are used to calculate the Infiltration Driving Force (IDF) and ultimately determine the infiltration rate for both summer and winter conditions.`
    }
    this.outputSummerInfiltration = new Field({
      name: 'Summer Infiltration',
      type: FieldType.AirFlow,
      basePath: 'infiltration',
      isOutput: true,
    })
    this.outputWinterInfiltration = new Field({
      name: 'Winter Infiltration',
      type: FieldType.AirFlow,
      basePath: 'infiltration',
      isOutput: true,
    })
    this.outputSummerInfiltration.makeUpdater((field) => {
      if (this.infiltrationEntryMethod.value == ManualOrAutomatic.Automatic) {
        field.visible = true;
        let ctx = CalcContext.create();
        let sampleTemps = this.proj.getSampleTemps();
        ctx.t_i = sampleTemps.t_i_summer;
        ctx.t_o = sampleTemps.t_o_summer;
        ctx.N_stories = this.proj.toplevelData.numberStoreys.value;
        ctx.avgCeilH = this.proj.toplevelData.averageCeilingHeight.value;
        field.value = this._calcInfiltration(ctx, false);
      } else {
        field.visible = false;
      }
    })
    this.outputWinterInfiltration.makeUpdater((field) => {
      if (this.infiltrationEntryMethod.value == ManualOrAutomatic.Automatic) {
        field.visible = true;
        let ctx = CalcContext.create();
        let sampleTemps = this.proj.getSampleTemps();
        ctx.t_i = sampleTemps.t_i_winter;
        ctx.t_o = sampleTemps.t_o_winter;
        ctx.N_stories = this.proj.toplevelData.numberStoreys.value;
        ctx.avgCeilH = this.proj.toplevelData.averageCeilingHeight.value;
        field.value = this._calcInfiltration(ctx, true);
      } else {
        field.visible = false;
      }
    })

    this.serFields = [
      'entryMethod',
      'totalVentilation',
      'continuousExhaust',
      'recoveryType',
      'hrvGroup',
      'ervGroup',
      'otherBalancedAirflows',
      'infiltrationEntryMethod',
      'infiltrationManual',
      'infiltrationAutomatic',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Ventilation & Infiltration',
      path: 'build',
    }
  }

  postReadFromJson(jsonData) {
    let hrvFlow = this.hrvGroup.getField('flow');
    if (hrvFlow.data.useDefault === undefined) {
      let ventilation = this.entryMethod.value == AutomaticOrManual.Automatic
        ? calc.calcQ_V(CalcContext.create(), this.proj.toplevelData.totalFloorArea.value,
          this.proj.toplevelData.numberBedrooms.value)
        : this.totalVentilation.value;
      let legacyDefault = this.proj.isMetric() ? 50 : 100;
      hrvFlow.data.useDefault = Math.abs(hrvFlow.displayValue - legacyDefault) < 0.01;
      hrvFlow.data.defaultValue = ventilation;
    }
    hrvFlow.isOutput = hrvFlow.data.useDefault;

    let exposedArea = this.infiltrationAutomatic.getField('totalExposedArea');
    if (exposedArea.data.useDefault === undefined) {
      let area = this.proj.walls.reduce((sum, wall) => sum + wall.getArea(), 0)
        + this.proj.roofs.reduce((sum, roof) => sum + roof.getArea(), 0);
      let legacyDefault = this.proj.isMetric() ? 350 : 3500;
      exposedArea.data.useDefault = Math.abs(exposedArea.displayValue - legacyDefault) < 0.01;
      exposedArea.data.defaultValue = area;
    }
    exposedArea.isOutput = exposedArea.data.useDefault;

    let fluePipeArea = this.infiltrationAutomatic.getField('fluePipeLeakageArea');
    let savedAutomaticData = jsonData.infiltrationAutomatic;
    if (!savedAutomaticData?.fluePipe) {
      let savedArea = savedAutomaticData?.fluePipeLeakageArea;
      let savedValue = savedArea?._value ?? savedArea?.value ?? 0;
      if (savedValue > 0) {
        this.infiltrationAutomatic.getField('fluePipe').value = YesNo.Yes;
      }
    }
    if (fluePipeArea.data.useDefault === undefined) {
      let defaultValue = this.infiltrationAutomatic.getField('fluePipe').value === YesNo.Yes ? 7.1 : 0;
      fluePipeArea.data.useDefault = Math.abs(fluePipeArea.value - defaultValue) < 0.01;
      fluePipeArea.data.defaultValue = defaultValue;
    }
    fluePipeArea.isOutput = fluePipeArea.data.useDefault;
  }

  calcOutputs(ctx) {
    ctx.startSection("Ventilation&Infiltration");
    let results = {
      heating: this._calcLoads(ctx, true),
      cooling: this._calcLoads(ctx, false),
    }
    ctx.log("");
    ctx.res.ventilationInfiltration = results;
    ctx.endSection();
  }

  _calcInfiltration(ctx, isHeating) {
    ctx.require('t_i');
    ctx.require('t_o');
    ctx.require('N_stories');
    ctx.require('avgCeilH');
    ctx.infiltrationEntryMethod = this.infiltrationEntryMethod.value;
    if (this.infiltrationEntryMethod.value == ManualOrAutomatic.Manual) {
      // Manual calculation of Q_i
      ctx.Q_i = isHeating ? this.infiltrationManual.getField('winterInfiltration').value
        : this.infiltrationManual.getField('summerInfiltration').value;
    } else {
      // Automatic calculation of Q_i
      let A_es = this.infiltrationAutomatic.getField('totalExposedArea').value;
      let A_ul = lookupData(calc.A_ul_Table, [
        this.infiltrationAutomatic.getField('buildingQuality').value,
      ]);
      ctx.A_L = ctx.eval('A_es * A_ul', {A_es, A_ul}, 'A_L');
      ctx.A_L_flue = this.infiltrationAutomatic.getField('fluePipeLeakageArea').value;
      ctx.IDF = calc.calcIDF(ctx, ctx.A_L_flue, ctx.A_L, ctx.avgCeilH*ctx.N_stories, ctx.t_i, ctx.t_o, isHeating);
      ctx.Q_i = ctx.eval('IDF * A_L', {IDF:ctx.IDF, A_L:ctx.A_L}, 'Q_i');
    }
    return ctx.Q_i;
  }

  _calcLoads(ctx, isHeating) {
    ctx.startSection(`${isHeating ? 'Heating' : 'Cooling'}`);

    let weatherData = ctx.toplevelData.locationData;
    ctx.elevation = weatherData.elevation;

    ctx.P_loc = psy.calcLocalPressure(ctx.elevation);
    ctx.C_s = 1.1 * ctx.P_loc / psy.P_std;
    ctx.C_t = 4.5 * ctx.P_loc / psy.P_std;
    ctx.C_l = 4840 * ctx.P_loc / psy.P_std;
    ctx.t_i = isHeating ? ctx.toplevelData.indoorWinterTemp
      : ctx.toplevelData.indoorSummerTemp;
    ctx.t_o = isHeating? ctx.designTemps.heating : ctx.designTemps.cooling;
    if (!isHeating) {
      ctx.t_wb_F = ctx.designTemps.coolingMCWB;
    }
    ctx.A_floor = ctx.toplevelData.totalFloorArea;
    ctx.avgCeilH = ctx.toplevelData.averageCeilingHeight;
    ctx.N_stories = ctx.toplevelData.numberStoreys;
   
    if (isHeating) {
      // For heating, we do not calculate latent loads
      [ctx.h_o, ctx.W_o] = ctx.call(psy.calcIFull, ctx.t_o, psy.RH_winter_outdoor, ctx.P_loc);
    } else {
      [ctx.h_i, ctx.W_i] = ctx.call(psy.calcIFull, ctx.t_i, ctx.toplevelData.indoorSummerHumidity, ctx.P_loc);
      [ctx.h_o, ctx.W_o] = ctx.call(psy.calcIFullKnownTwb, ctx.t_o, ctx.t_wb_F, ctx.P_loc);
    }

    if (this.entryMethod.value == ManualOrAutomatic.Manual) {
      ctx.Q_sup = this.totalVentilation.value;
    } else {
      ctx.N_br = ctx.toplevelData.numberBedrooms;
      ctx.Q_sup = calc.calcQ_V(ctx, ctx.A_floor, ctx.N_br);
    }
    ctx.Q_exh = this.continuousExhaust.value;
    ctx.Q_bal = ctx.eval('min(Q_sup, Q_exh)',
      {Q_sup: ctx.Q_sup, Q_exh: ctx.Q_exh}, 'Q_bal');
    ctx.Q_unbal = ctx.eval('max(Q_sup, Q_exh) - Q_bal',
        {Q_sup:ctx.Q_sup, Q_exh:ctx.Q_exh, Q_bal:ctx.Q_bal}, 'Q_unbal');

    ctx.Q_i = this._calcInfiltration(ctx, isHeating);
    ctx.Q_vi = ctx.eval('max(Q_unbal, Q_i + 0.5*Q_unbal)',
      {Q_unbal:ctx.Q_unbal,Q_i:ctx.Q_i}, 'Q_vi');

    let heatingSign = isHeating ? 1 : -1;
    if (this.recoveryType.value == RecoveryType.None) {
      ctx.q_A_s = ctx.eval('Q_vi*C_s*heatingSign*(t_i - t_o)',
        {heatingSign}, 'q_A_s');
      ctx.q_A_l = isHeating ? 0 : ctx.eval('Q_vi*C_l*heatingSign*(W_i - W_o)',
        {heatingSign}, 'q_A_l');
    } else if (this.recoveryType.value == RecoveryType.HRV) {
      ctx.Eff_s = this.hrvGroup.getField(
        isHeating ? 'winterEfficiency' : 'summerEfficiency').value;
      ctx.Q_HRV = this.hrvGroup.getField('flow').value;
      ctx.Q_bal_other = this.otherBalancedAirflows.value;
      ctx.q_A_s = ctx.eval('C_s*(Q_vi + (1 - Eff_s/100.0)*Q_HRV + Q_bal_other)*heatingSign*(t_i - t_o)',
        {heatingSign}, 'q_A_s');
      ctx.q_A_l = isHeating ? 0 : ctx.eval('C_l*(Q_vi + Q_bal_other)*heatingSign*(W_i - W_o)',
        {heatingSign}, 'q_A_l');
    }

    let q_A_s = ctx.q_A_s;
    let q_A_l = ctx.q_A_l;
    ctx.endSection();

    return {
      sensible: q_A_s,
      latent: q_A_l,
    };
  }
};
setupClass(HouseVentilationInfiltration)
